import type { AppDataTableColumn } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { AppIconButton, AppTooltip } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { SalesStageBadge } from '@opportunities/-components/SalesStageBadge'
import { Copy, MessageCircle } from 'lucide-react'
import { INCENTIVE_SALES_STAGE_MAP } from 'shared'
import type { OpportunityIncentive } from '@/lib'
import { formatDate, formatNumber, toDateTime } from '@/lib/format-utils'
import { convertPeriodOnToFiscalPeriod } from '@/lib/period-utils'

// TODO: libにしたほうがよさそう
/**
 * 売上月を「yyyy年MM月」形式にフォーマット
 */
function formatSalesOn(salesOn: string): string {
  const [year, month] = salesOn.split('-')
  return `${year}年${month}月`
}

/**
 * インセンティブ一覧テーブルのカラム定義を生成
 */
export function createIncentiveColumns(
  copyToClipboard: (item: OpportunityIncentive) => void,
): AppDataTableColumn<OpportunityIncentive>[] {
  return [
    {
      key: 'opportunity_id',
      label: '管理ID',
      accessor: (row) => row.opportunity_id,
      cellClassName: 'text-center',
    },
    {
      key: 'salesforce_project_name',
      label: 'SF案件名',
      accessor: (row) => row.salesforce_project_name,
      render: (_row, value) => <span>{(value as string | null) || 'ー'}</span>,
    },
    {
      key: 'sales_id',
      label: '売上情報ID',
      accessor: (row) => row.sales_id,
      render: (_row, value) => <span>{(value as string | null) || 'ー'}</span>,
    },
    {
      key: 'sales_on',
      label: '売上月',
      accessor: (row) => row.sales_on,
      render: (_row, value) => <span>{formatSalesOn(value as string)}</span>,
    },
    {
      key: 'sales_amount',
      label: 'オポ金額',
      accessor: (row) => row.sales_amount,
      render: (_row, value) => <span className='text-right'>{formatNumber(value as number)}</span>,
      footer: (data) => {
        const total = data.reduce((sum, item) => sum + (item.sales_amount || 0), 0)
        return <span className='text-right text-xs font-bold'>{formatNumber(total)}</span>
      },
      cellClassName: 'text-right',
      headerClassName: 'text-right',
      footerClassName: 'text-right',
    },
    {
      key: 'sales_stage',
      label: 'Sales Stage',
      accessor: (row) => (row.sales_stage_id ? (INCENTIVE_SALES_STAGE_MAP[row.sales_stage_id]?.text ?? '') : ''),
      render: (row) => <SalesStageBadge stageId={row.sales_stage_id} variant='incentive' />,
      cellClassName: 'text-center',
    },
    {
      key: 'previously_closed_sales_stage',
      label: '失注前Sales Stage',
      accessor: (row) =>
        row.previously_closed_sales_stage_id
          ? (INCENTIVE_SALES_STAGE_MAP[row.previously_closed_sales_stage_id]?.text ?? '')
          : '',
      render: (row) => <SalesStageBadge stageId={row.previously_closed_sales_stage_id} variant='incentive' />,
      cellClassName: 'text-center',
    },
    {
      key: 'incentive_coefficient',
      label: '係数',
      accessor: (row) => row.incentive_coefficient,
      render: (_row, value) => <span>{((value as number) * 100).toFixed(1)}%</span>,
    },
    {
      key: 'incentive_amount',
      label: '報酬金額',
      accessor: (row) => row.incentive_amount,
      render: (_row, value) => <span className='text-right'>{formatNumber(value as number)}</span>,
      footer: (data) => {
        const total = data.reduce((sum, item) => sum + (item.incentive_amount || 0), 0)
        return <span className='text-right text-xs font-bold'>{formatNumber(total)}</span>
      },
      cellClassName: 'text-right',
      headerClassName: 'text-right',
      footerClassName: 'text-right',
    },
    {
      key: 'period',
      label: '支払対象期間',
      accessor: (row) => convertPeriodOnToFiscalPeriod(row.period),
      cellClassName: 'text-center',
    },
    {
      key: 'notes',
      label: '備考',
      accessor: (row) => row.notes,
      render: (_row, value) => {
        if (!value) return null

        return (
          <AppTooltip content={<p className='text-xs'>{value as string}</p>}>
            <div className='flex justify-center'>
              <MessageCircle className='w-4 h-4 text-muted-foreground' />
            </div>
          </AppTooltip>
        )
      },
      cellClassName: 'text-center',
    },
    {
      key: 'updated_at',
      label: '更新日時',
      accessor: (row) => row.updated_at,
      render: (_row, value) => {
        if (!value) return '-'
        return (
          <AppTooltip content={toDateTime(value as number)?.toFormat('yyyy/MM/dd HH:mm:ss') ?? '-'}>
            <span className='cursor-help'>{formatDate(value as number, 'relative')}</span>
          </AppTooltip>
        )
      },
    },
    {
      key: 'action',
      label: 'Action',
      accessor: (_row) => null,
      render: (row) => (
        <AppIconButton onClick={() => copyToClipboard(row)} description='CSV形式で行をクリップボードにコピー'>
          <Copy />
        </AppIconButton>
      ),
      sortable: false,
      cellClassName: 'w-[60px] text-center',
      headerClassName: 'w-[60px] text-center',
    },
  ]
}
